import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import { encryptDataHybrid, apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { IoWarningOutline } from "react-icons/io5";

function PaymentConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const [showPaymentSummarySheet, setShowPaymentSummarySheet] = useState(false);
  const [balance, setBalance] = useState("0");
  const [customAmount, setCustomAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const paymentDetails = location.state;
  const [locationLoading, setLocationLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);


  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const result = await apiCall("balance", {}, true);
        if (result.status === true) {
          setBalance(result.data.balance);
        } else {
          toast.error(result.message || "An error occurred. Please try again.");
        }
      } catch (error) {
        toast.error("Network error. Please try again.");
      }
    };

    fetchBalance();

    // For DTH Recharge, always set the amount from paymentDetails
    if (paymentDetails?.service === "DTH Recharge") {
      setCustomAmount(paymentDetails?.amount?.toString() || "");
    }
    // For Bill Payment with custom amount
    else if (paymentDetails?.service === "Bill Payment" && paymentDetails?.serviceSpecificData?.billAmount) {
      setCustomAmount(paymentDetails.serviceSpecificData.billAmount);
    }
  }, [paymentDetails]); // Add paymentDetails as dependency


  if (!paymentDetails) {
    return <Navigate to="/" replace />;
  }

  const { service, operator, logo, operator_id, number, amount, consumerName } = paymentDetails;

  // Service-specific data (only use if available)
  const serviceSpecificData = paymentDetails.serviceSpecificData || {};

  console.log("received", serviceSpecificData);

  // Get plan description (if available from service-specific data)
  const planDesc = serviceSpecificData.planDesc || "";

  // Get first line of plan description
  const getFirstLine = (description) => {
    if (!description) return "";
    const firstLine = description.split('\n')[0];
    if (description.split('\n').length > 1 || firstLine.length > 50) {
      return firstLine;
    }
    return firstLine;
  };

  const needsSeeMore = (description) => {
    if (!description) return false;
    return description.split('\n').length > 1 || description.length > 60;
  };

  // Calculate payment details
  const balanceNum = parseFloat(balance) || 0;

  // Determine the amount based on service
  let amountNum = parseFloat(amount) || 0;

  // For DTH Recharge - use custom amount if set, else use passed amount
  if (service === "DTH Recharge" && customAmount) {
    amountNum = parseFloat(customAmount) || 0;
  }
  // For Bill Payment with custom amount
  else if (service === "Bill Payment" && customAmount) {
    amountNum = parseFloat(customAmount) || 0;
  }

  // Determine wallet deduction and payable amount
  const walletDeduction = balanceNum >= amountNum ? amountNum : balanceNum;
  const payableAmount = balanceNum >= amountNum ? 0 : (amountNum - balanceNum);

  // Get bill payment range
  const billMin = parseFloat(serviceSpecificData?.range?.min) || 0;
  const billMax = parseFloat(serviceSpecificData?.range?.max) || 0;

  // Get DTH amount range
  const dthMin = parseFloat(serviceSpecificData?.range?.min) || 0;
  const dthMax = parseFloat(serviceSpecificData?.range?.max) || 0;

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);
    setAmountError(""); // Clear error when user types
  };

  const validateAmount = () => {
    const numValue = parseFloat(customAmount);
    if (!customAmount || isNaN(numValue)) {
      setAmountError("Please enter a valid amount");
      return false;
    }

    // Validate based on service type
    if (service === "Bill Payment" && serviceSpecificData.acceptPartPay) {
      if (numValue < billMin) {
        setAmountError(`Minimum amount is ₹${billMin}`);
        return false;
      }
      if (numValue > billMax) {
        setAmountError(`Maximum amount is ₹${billMax}`);
        return false;
      }
    } else if (service === "DTH Recharge") {
      if (numValue < dthMin) {
        setAmountError(`Minimum amount is ₹${dthMin}`);
        return false;
      }
      if (numValue > dthMax) {
        setAmountError(`Maximum amount is ₹${dthMax}`);
        return false;
      }
    }

    return true;
  };

  const handleLocation = () => {
    // Validate amount for DTH Recharge and Bill Payment
    if ((service === "DTH Recharge" || (service === "Bill Payment" && serviceSpecificData.acceptPartPay)) && customAmount) {
      if (!validateAmount()) {
        return;
      }
    }

    setLocationLoading(true);
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "GET_LOCATION_INFO"
        })
      );
    } else {
      toast.error("Location service not available");
      setLocationLoading(false);
    }
  };


  window.addEventListener("message", async (event) => {
    try {
      // Check if event.data is a string or object
      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else {
        // If it's already an object, use it directly
        data = event.data;
      }

      if (data.type === "LOCATION_INFO") {
        setUserLocation(data);
        setLocationLoading(false);
        setShowPaymentSummarySheet(true);
      }
    } catch (e) {
      setLocationLoading(false);
      toast.error("Error in message listener:", e.message);
    }
  });

  const handleConfirm = async () => {
    //wallet
    if (payableAmount === 0) {
      setLoading(true);
      // Close payment summary sheet if open
      setShowPaymentSummarySheet(false);

      try {
        const storedUserData = localStorage.getItem("credentials");
        const parsedData = JSON.parse(storedUserData);

        const user_id = parsedData?.user_id || '';
        const token = parsedData?.login_token || '';

        // Use custom amount for DTH and Bill Payment if set
        let finalAmount = amount;
        if (service === "DTH Recharge" && customAmount) {
          finalAmount = customAmount;
        } else if (service === "Bill Payment" && customAmount) {
          finalAmount = customAmount;
        }

        const payload = {
          consumerName,
          number,
          operator_id,
          amount: finalAmount,
          client_order_id: "WP" + Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 90 + 10),
          longitude: userLocation?.longitude || null,
          latitude: userLocation?.latitude || null,
          address: userLocation?.address || null
        };

        if (service === "Bill Payment") {
          payload.additional = serviceSpecificData.pass_data;
          payload.form = serviceSpecificData.form;
        }

        if (service === "DTH Recharge") {
          // Add DTH specific data
          payload.additional = {
            range: serviceSpecificData.range,
            dueDate: serviceSpecificData.dueDate || "A",
            balance: serviceSpecificData.balance || "0",
            form: serviceSpecificData.form || {}
          };
        }

        // 🔐 Encrypt full request
        const encrypted = encryptDataHybrid({
          appPlatform: '',
          appVersion: '',
          buildNumber: '',
          user_id,
          token,
          payload,
        });

        if (!encrypted) throw new Error("Encryption failed");

        const timestamp = Math.floor(Date.now() / 1000);

        const response = await fetch(`${process.env.REACT_APP_API_URL}recharge.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${timestamp * 784116}`,
          },
          body: JSON.stringify({
            encryptedData: encrypted.encryptedData,
            encryptedKey: encrypted.encryptedKey,
          }),
        });

        if (!response.ok) throw new Error("Server error");

        const result = await response.json();

        if (result.status) {
          setLoading(false);
          navigate("/payment-status", {
            state: {
              status: result.order_details.status,
              amount: finalAmount,
              number: number,
              order_id: result.order_details.order_id,
              operator_ref: result.order_details.operator_ref,
              cashback: result.order_details.cashback,
              message: result.message
            }
          });
        } else {
          navigate("/payment-status", {
            state: {
              status: "Failed",
              amount: finalAmount,
              number: number,
              order_id: result.order_details.order_id,
              operator_ref: result.order_details.operator_ref,
              cashback: result.order_details.cashback,
              message: result.message
            }
          });
        }
      } catch {
        setLoading(false);
        navigate("/payment-status", {
          state: {
            status: 'Processed',
            amount: amountNum,
            number: number,
            order_id: '',
            operator_ref: '',
            cashback: '',
            message: 'Unexpcted error for server'
          }
        });
      }
    }
    //direct payment
    else if (payableAmount > 0) {
      setLoading(true);
      // Close payment summary sheet if open
      setShowPaymentSummarySheet(false);

      try {
        // Use custom amount for DTH and Bill Payment if set
        let finalAmount = amount;
        if (service === "DTH Recharge" && customAmount) {
          finalAmount = customAmount;
        } else if (service === "Bill Payment" && customAmount) {
          finalAmount = customAmount;
        }

        const client_order_id = "AP" + Math.floor(Date.now() / 1000) + Math.floor(Math.random() * 90 + 10);

        const utility_payload = {
          consumerName,
          number,
          operator_id,
          amount: finalAmount,
          client_order_id
        };

        if (service === "Bill Payment") {
          utility_payload.additional = serviceSpecificData.pass_data;
          utility_payload.form = serviceSpecificData.form;
        }

        if (service === "DTH Recharge") {
          // Add DTH specific data
          utility_payload.additional = {
            range: serviceSpecificData.range,
            dueDate: serviceSpecificData.dueDate || "A",
            balance: serviceSpecificData.balance || "0",
            form: serviceSpecificData.form || {}
          };
        }

        const payload = {
          amount: payableAmount,
          platform: 1,
          utility_payload,
          client_order_id,
          longitude: userLocation?.longitude || null,
          latitude: userLocation?.latitude || null,
          address: userLocation?.address || null
        }

        const result = await apiCall(
          "payment",
          payload,
          true
        );

        if (result.status === true) {
          if (result.order_info.payment_method === "intent") {
            navigate('/payment/upi-intent', { state: { url: result.order_info.payment_url, payment_order_id: result.order_info.order_id, client_order_id, utility_payload } });
          } else if (result.order_info.payment_method === "web") {
            toast.error("Web method not installed");
          } else if (result.order_info.payment_method === "razorpay-sdk") {
            toast.error("Razorpay SDK not installed");
          } else if (result.order_info.payment_method === "cashfree-sdk") {
            toast.error("Cashfree SDK not installed");
          }
        } else {
          toast.error(result.message || "Failed to fetch bill");
        }
      } catch {
        setLoading(false);
        toast.error("Network error. Please try again.");
      }
    }
    else {
      toast.error("Payment criteria do not match.");
    }
  };

  // Helper function to get color based on biller name (for dynamic billers)
  const getBillerColor = (billerName) => {
    const colors = ["#0277BD", "#01579B", "#006064", "#004D40", "#1B5E20", "#E65100", "#B71C1C", "#311B92", "#4527A0", "#F57F17", "#0D47A1", "#BF360C", "#2E7D32", "#C62828", "#283593"];
    let hash = 0;
    for (let i = 0; i < billerName.length; i++) {
      hash = billerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getBillerLogo = (billerName) => {
    // Get first two letters of each word
    const words = billerName.split(' ');
    if (words.length === 1) {
      return billerName.substring(0, 2).toUpperCase();
    }
    return (words[0].charAt(0) + (words[1].charAt(0) || words[0].charAt(1))).toUpperCase();
  };

  const info = {
    logo: getBillerLogo(operator),
    color: getBillerColor(operator)
  };

  return (
    <div className="flex flex-col h-screen" style={{ background: APP_THEME.bgColor }}>
      <Header title="Pay Now" showBack={true} />

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto pb-4">

        <div className="p-4">
          <div
            className="p-3 bg-surface rounded-xl border cursor-pointer flex items-center gap-3.5 transition-all duration-200 active:scale-98 hover:shadow-md"
            style={{
              background: APP_THEME.surfaceColor,
              borderColor: APP_THEME.borderColor,
            }}
          >
            {/* Biller Logo */}
            {logo ? (
              <img
                src={logo}
                alt={operator}
                className="w-11 h-11 rounded-xl object-cover flex-shrink-0"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center text-[14px] font-extrabold text-white flex-shrink-0"
              style={{
                background: info.color,
                fontFamily: APP_THEME.fontFamily,
                display: logo ? 'none' : 'flex'
              }}
            >
              {info.logo}
            </div>

            <div className="flex-1">
              <span className="text-sm block" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                {operator}
              </span>
              <span className="text-sm block" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                {number}
              </span>
            </div>
          </div>
        </div>

        {/* Service Summary Card */}
        <div className="mx-4 p-5 rounded-2xl border"
          style={{
            background: APP_THEME.surfaceColor,
            borderColor: APP_THEME.borderColor,
            borderRadius: APP_THEME.borderRadiusCard,
          }}
        >
          <h3 className="text-base font-semibold mb-4" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
            Service Summary
          </h3>

          <div className="space-y-2.5">
            <div className="flex justify-between">
              <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Service</span>
              <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{service}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Number/ID</span>
              <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{number}</span>
            </div>

            {/* Consumer Name for DTH */}
            {consumerName && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Consumer Name</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{consumerName}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Amount</span>
              <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{amount}</span>
            </div>

            {/* Service-specific data display */}
            {service === "Mobile Recharge" && serviceSpecificData.circle && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Circle</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.circle}</span>
              </div>
            )}

            {service === "Mobile Recharge" && serviceSpecificData.data_pack && serviceSpecificData.data_pack !== "N/A" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Data</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.data_pack}</span>
              </div>
            )}

            {service === "Mobile Recharge" && serviceSpecificData.validaty && serviceSpecificData.validaty !== "N/A" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Validity</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.validaty}</span>
              </div>
            )}

            {service === "Mobile Recharge" && planDesc && (
              <div>
                <div className="flex justify-between items-start">
                  <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Plan</span>
                </div>
                <div className="text-sm mt-1 leading-relaxed" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                  {getFirstLine(planDesc)}
                  {needsSeeMore(planDesc) && (
                    <button
                      onClick={() => setShowDetailsSheet(true)}
                      className="font-semibold ml-1"
                      style={{ color: APP_THEME.primaryColor }}
                    >
                      ...see more
                    </button>
                  )}
                </div>
              </div>
            )}

            {service === "Bill Payment" && serviceSpecificData.billNumber && serviceSpecificData.billNumber !== "N/A" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Bill Number</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.billNumber}</span>
              </div>
            )}

            {service === "Bill Payment" && serviceSpecificData.billDate && serviceSpecificData.billDate !== "N/A" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Bill Date</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.billDate}</span>
              </div>
            )}

            {service === "Bill Payment" && serviceSpecificData.dueDate && serviceSpecificData.dueDate !== "N/A" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Due Date</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.dueDate}</span>
              </div>
            )}

            {service === "Bill Payment" && serviceSpecificData.billAmount && serviceSpecificData.billAmount !== "0" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Bill Amount</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.billAmount}</span>
              </div>
            )}

            {service === "Bill Payment" && serviceSpecificData.billConvenienceFee && serviceSpecificData.billConvenienceFee !== "0" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Convenience Fees</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.billConvenienceFee}</span>
              </div>
            )}

            {service === "Bill Payment" && serviceSpecificData.billNetAmount && serviceSpecificData.billNetAmount !== "0" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Net Amount</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.billNetAmount}</span>
              </div>
            )}

            {/* DTH Specific Details */}
            {service === "DTH Recharge" && serviceSpecificData.dueDate && serviceSpecificData.dueDate !== "A" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Due Date</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.dueDate}</span>
              </div>
            )}

            {service === "DTH Recharge" && serviceSpecificData.balance && serviceSpecificData.balance !== "0" && (
              <div className="flex justify-between">
                <span className="text-sm" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Account Balance</span>
                <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{serviceSpecificData.balance}</span>
              </div>
            )}

            {service === "Bill Payment" &&
              Array.isArray(serviceSpecificData.additional) &&
              serviceSpecificData.additional.map((item, index) => (
                <div key={index} className="flex justify-between gap-3">
                  <span
                    className="text-sm flex-shrink-0"
                    style={{
                      color: APP_THEME.textSecondary,
                      fontFamily: APP_THEME.fontFamily
                    }}
                  >
                    {item.infoName}
                  </span>

                  <span
                    className="text-sm font-semibold text-right break-all max-w-[60%]"
                    style={{
                      color: APP_THEME.textPrimary,
                      fontFamily: APP_THEME.fontFamily
                    }}
                  >
                    {item.infoValue}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Warning Alert for operator_id 114 */}
        {operator_id === "114" && (
          <div className="mx-4 mt-4 p-3 rounded-lg text-xs"
            style={{
              border: `1.5px solid ${APP_THEME.errorColor}`,
              background: `${APP_THEME.errorColor}08`,
              color: APP_THEME.errorColor,
            }}
          >
            <div className="flex items-start gap-2">
              <IoWarningOutline size={18} className="flex-shrink-0 mt-0.5" />
              <span>
                The amount is not for Google Pay/Gpay payment transfer, it is a redeem code for Games, Subscription and others.
              </span>
            </div>
          </div>
        )}

        {/* Amount Input - Always shown for DTH Recharge */}
        {service === "DTH Recharge" && (
          <div className="mx-4 mt-4 p-5 rounded-2xl border"
            style={{
              background: APP_THEME.surfaceColor,
              borderColor: APP_THEME.borderColor,
              borderRadius: APP_THEME.borderRadiusCard,
            }}
          >
            <h3 className="text-base font-semibold mb-4" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
              Enter Amount
            </h3>

            <div className="flex flex-col space-y-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-semibold" style={{ color: APP_THEME.textPrimary }}>
                  ₹
                </span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => handleCustomAmountChange(e.target.value)}
                  className={`w-full rounded-lg border px-8 py-3 text-lg font-semibold focus:outline-none focus:ring-2 ${amountError ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                  style={{
                    color: APP_THEME.textPrimary,
                    background: APP_THEME.bgColor,
                    borderColor: amountError ? '#ef4444' : APP_THEME.borderColor,
                    fontFamily: APP_THEME.fontFamily,
                  }}
                  placeholder={`Enter amount`}
                />
              </div>
              {amountError ? (
                <div className="text-sm text-red-500">{amountError}</div>
              ) : (
                <div className="flex justify-between text-xs" style={{ color: APP_THEME.textSecondary }}>
                  <span>Min: ₹{dthMin}</span>
                  <span>Max: ₹{dthMax}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bill Payment Amount Input */}
        {service === "Bill Payment" && serviceSpecificData.acceptPartPay && (
          <div className="mx-4 mt-4 p-5 rounded-2xl border"
            style={{
              background: APP_THEME.surfaceColor,
              borderColor: APP_THEME.borderColor,
              borderRadius: APP_THEME.borderRadiusCard,
            }}
          >
            <h3 className="text-base font-semibold mb-4" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
              Enter Amount
            </h3>

            <div className="flex flex-col space-y-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-semibold" style={{ color: APP_THEME.textPrimary }}>
                  ₹
                </span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => handleCustomAmountChange(e.target.value)}
                  className={`w-full rounded-lg border px-8 py-3 text-lg font-semibold focus:outline-none focus:ring-2 ${amountError ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                  style={{
                    color: APP_THEME.textPrimary,
                    background: APP_THEME.bgColor,
                    borderColor: amountError ? '#ef4444' : APP_THEME.borderColor,
                    fontFamily: APP_THEME.fontFamily,
                  }}
                  placeholder={`Enter amount`}
                />
              </div>
              {amountError ? (
                <div className="text-sm text-red-500">{amountError}</div>
              ) : (
                <div className="flex justify-between text-xs" style={{ color: APP_THEME.textSecondary }}>
                  <span>Min: ₹{billMin}</span>
                  <span>Max: ₹{billMax}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirm Button - Fixed at bottom */}
      <div className="flex-shrink-0 mx-4 mb-4">
        <button
          onClick={handleLocation}
          disabled={loading || locationLoading || (service === "DTH Recharge" && !customAmount)}
          className="w-full py-3.5 rounded-lg text-base font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            background: loading || locationLoading || (service === "DTH Recharge" && !customAmount) ? APP_THEME.textSecondary : APP_THEME.primaryColor,
            color: APP_THEME.textOnPrimary,
            borderRadius: APP_THEME.borderRadiusBtn,
            fontFamily: APP_THEME.fontFamily,
            opacity: (loading || locationLoading) ? 0.7 : 1,
            cursor: (loading || locationLoading) ? 'not-allowed' : 'pointer',
          }}
        >
          {(loading || locationLoading) ? (
            <>
              <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `2px solid #ffffff33`, borderTopColor: "#fff" }} />
              <span>Processing...</span>
            </>
          ) : (
            `Pay Now ₹${amountNum.toFixed(2)}`
          )}
        </button>
      </div>

      {/* Location Loading Overlay */}
      {locationLoading && (
        <>
          {/*
          <div
            className="fixed inset-0 backdrop-blur-md bg-black/40 z-[1200] animate-fadeIn"
            style={{ backdropFilter: 'blur(8px)' }}
          />
          <div className="fixed inset-0 z-[1201] flex items-center justify-center">
            <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl animate-scaleIn max-w-[320px] w-full mx-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-gray-200 border-t-4 animate-spin"
                  style={{ borderTopColor: APP_THEME.primaryColor }} />
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-lg font-semibold" style={{ color: APP_THEME.textPrimary }}>
                  Fetching Location
                </h3>
                <p className="text-sm mt-1" style={{ color: APP_THEME.textSecondary }}>
                  Please allow location access
                </p>
              </div>
            </div>
          </div>
          */}
        </>
      )}

      {/* Payment Summary Bottom Sheet */}
      {showPaymentSummarySheet && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[1100] animate-fadeIn"
            onClick={() => setShowPaymentSummarySheet(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-[1101] rounded-t-2xl animate-slideUp max-h-[85vh] overflow-y-auto"
            style={{ background: APP_THEME.bgColor }}
          >
            {/* Header */}
            <div className="sticky top-0 pt-3 pb-2 px-5 border-b" style={{ background: APP_THEME.bgColor, borderColor: APP_THEME.borderColor }}>
              <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: APP_THEME.borderColor }} />
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold" style={{ color: APP_THEME.textPrimary }}>Payment Summary</h3>
                <button onClick={() => setShowPaymentSummarySheet(false)} className="p-1 rounded-full active:scale-90 transition-transform">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 pb-6">

              {/* Payment Breakdown */}
              <div className="mb-4 p-4 rounded-xl" style={{ background: APP_THEME.surfaceColor }}>
                <h4 className="text-sm font-semibold mb-3" style={{ color: APP_THEME.textPrimary }}>Payment Breakdown</h4>

                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm" style={{ color: APP_THEME.textSecondary }}>Recharge Amount</span>
                  <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary }}>₹{amountNum.toFixed(2)}</span>
                </div>

                {walletDeduction > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm" style={{ color: APP_THEME.textSecondary }}>Wallet Deduction</span>
                    <span className="text-sm font-semibold text-green-600">- ₹{walletDeduction.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm" style={{ color: APP_THEME.textSecondary }}>Platform Fees</span>
                  <span className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary }}>+ ₹0.00</span>
                </div>

                <div className="h-px my-3" style={{ background: APP_THEME.borderColor }} />

                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold" style={{ color: APP_THEME.textPrimary }}>Total Payable</span>
                  <span className="text-xl font-bold" style={{ color: APP_THEME.primaryColor }}>
                    ₹{payableAmount.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Wallet Balance */}
              <div className="mb-4 p-4 rounded-xl flex justify-between items-center" style={{ background: APP_THEME.surfaceColor }}>
                <span className="text-sm" style={{ color: APP_THEME.textSecondary }}>Wallet Balance</span>
                <span className="text-base font-semibold" style={{ color: APP_THEME.textPrimary }}>₹{balanceNum.toFixed(2)}</span>
              </div>

              {/* Confirm Button */}
              <button
                onClick={handleConfirm}
                disabled={loading}
                className="w-full py-3.5 rounded-lg text-base font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{
                  background: loading ? APP_THEME.textSecondary : APP_THEME.primaryColor,
                  color: APP_THEME.textOnPrimary,
                  borderRadius: APP_THEME.borderRadiusBtn,
                  fontFamily: APP_THEME.fontFamily,
                }}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `2px solid #ffffff33`, borderTopColor: "#fff" }} />
                    <span>Processing...</span>
                  </>
                ) : (
                  `Confirm Payment ₹${payableAmount.toFixed(2)}`
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Plan Details Bottom Sheet */}
      {showDetailsSheet && planDesc && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[1100] animate-fadeIn"
            onClick={() => setShowDetailsSheet(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-[1101] rounded-t-2xl animate-slideUp max-h-[80vh] overflow-y-auto"
            style={{ background: APP_THEME.bgColor }}
          >
            {/* Header */}
            <div className="sticky top-0 pt-3 pb-2 px-5 border-b" style={{ background: APP_THEME.bgColor, borderColor: APP_THEME.borderColor }}>
              <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: APP_THEME.borderColor }} />
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold" style={{ color: APP_THEME.textPrimary }}>Plan Details</h3>
                <button onClick={() => setShowDetailsSheet(false)} className="p-1 rounded-full active:scale-90 transition-transform">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 pb-6">
              {/* Amount */}
              <div className="text-2xl font-bold mb-4" style={{ color: APP_THEME.textPrimary }}>
                ₹{amountNum.toFixed(2)}
              </div>

              {/* Data & Validity Cards */}
              {serviceSpecificData.data_pack && serviceSpecificData.data_pack !== "N/A" && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-xl" style={{ background: APP_THEME.surfaceColor }}>
                    <div className="text-xs mb-1" style={{ color: APP_THEME.textSecondary }}>Data</div>
                    <div className="text-base font-semibold" style={{ color: APP_THEME.textPrimary }}>
                      {serviceSpecificData.data_pack}
                    </div>
                  </div>
                  <div className="p-3 rounded-xl" style={{ background: APP_THEME.surfaceColor }}>
                    <div className="text-xs mb-1" style={{ color: APP_THEME.textSecondary }}>Validity</div>
                    <div className="text-base font-semibold" style={{ color: APP_THEME.textPrimary }}>
                      {serviceSpecificData.validaty}
                    </div>
                  </div>
                </div>
              )}

              {/* Talktime if available */}
              {serviceSpecificData.talktime && serviceSpecificData.talktime !== "N/A" && (
                <div className="mb-4 p-3 rounded-xl" style={{ background: APP_THEME.surfaceColor }}>
                  <div className="text-xs mb-1" style={{ color: APP_THEME.textSecondary }}>Talktime</div>
                  <div className="text-base font-semibold" style={{ color: APP_THEME.textPrimary }}>
                    {serviceSpecificData.talktime}
                  </div>
                </div>
              )}

              {/* Full Description */}
              <div>
                <div className="text-sm font-semibold mb-2" style={{ color: APP_THEME.textPrimary }}>Description</div>
                <div className="text-sm leading-relaxed" style={{ color: APP_THEME.textSecondary }}>
                  {planDesc?.split('\n').map((line, idx) => (
                    <p key={idx} className="mb-1.5">{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <Toaster position="bottom-center" />

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-spin {
            animation: spin 0.8s linear infinite;
          }
          .animate-fadeIn {
            animation: fadeIn 0.2s ease forwards;
          }
          .animate-slideUp {
            animation: slideUp 0.3s ease forwards;
          }
          .active\\:scale-98:active {
            transform: scale(0.98);
          }
          .active\\:scale-90:active {
            transform: scale(0.9);
          }
        `}
      </style>
    </div>
  );
}

export default PaymentConfirmation;